import { Audio } from "../components/audioplayer.js"
import {getSingularOrPlural, LIKE} from "../ui/like.js"
import { createMenuButton, setSignInModal, EditModal, LongModal, createReportUserButton, SubmitModal } from "../components/build-modal.js"
import { getShowMoreText } from "../helpers/collapse.js"
import { buildBadge } from "../../utils/common.js"
import {postComment, buildCommentContent, commentDeletionModalElement} from "../ui/comment.js"
import {getTimeAgo} from "../helpers/time-converter.js"
import { el, mount, setAttr } from "redom";
import { RecordPageFollowManager } from "../ui/userfollow.js"
import { openOrReturnToUserPage, openUserPageOnClick } from "./userpage.js"
import { createRecordShareModal } from "../components/build-share-modal.js"
import { createFormData } from "../../utils/formdata.js"
import { fetchPostValidated, handleErrorWithAlert } from "../../utils/fetch-json.js"
import { isBlocked } from "../helpers/report.js"
import type { AudioRecord, CommentWithUser, MaybeUser, ModalButton, RecordDetails } from "#/types"
import { maybeMeIsMe } from "../../utils/authUser.js"

// Note: This is very different from the createRecordDeletionModalElement in build-modal.js.
// This handles a single record while one in build-modal handles multiple records.
const createRecordDeletionModal = (currentFile: AudioRecord, action: () => void) => {
    const content = el("p#delete-single-record-title", { style: "text-align: center; text-overflow: ellipsis; overflow: hidden; font-size: 25; margin: 0px;" }, currentFile.title)
    const modal = new SubmitModal("deleteConfirm", "DELETE RECORD?", content, "DELETE", () => {
        modal.disableSubmitButton()
        fetchPostValidated("/v1/r/" + currentFile.id + "/delete")
            .then(() => action())
            .catch(handleErrorWithAlert)
            .finally(() => modal.enableSubmitButton())
    })
    return modal
}

export const buildRecordUnavailable = () =>
    el("", { style:"padding:50px 0px" }, el("h5.recordStatusNotOK", { textContent: "Record is not available.", className: "record-inaccessible" }))

export const buildRecordPost = (curPost: RecordDetails, numberOfCommentsToShow: number, me: MaybeUser) => {
    const { record: currentFile, comments, uploader, embed, playlist } = curPost
    if(currentFile.status == "Deleted" || currentFile.status == "Failed")
        return buildRecordUnavailable()

    const recordElements = document.createDocumentFragment()
    mount(recordElements, commentDeletionModalElement)
    const button = Audio.isPlaying ? "pause" : "play"
    const isMyRecord = maybeMeIsMe(me) && me.get.id == uploader.id
    const editRecord = () => {
        const name = (document.getElementById("name") as HTMLInputElement).value
        const fd = createFormData( { name } )
        const url = `/v1/r/${currentFile.id}/edit`
        return fetchPostValidated(url, fd).then(() => {
            const recordTitle = document.getElementById("record-title")
            if (recordTitle) recordTitle.textContent = name
            document.title = `${name} - Soundcorset`
            const currentPlayer = document.getElementById("audioPlayerTitle")
            if (currentPlayer) currentPlayer.innerText = name
        }).catch(handleErrorWithAlert)
    }
    const likeModal = new LongModal("likesModal", "LIKES", el("p#modalLikeContext.noSpace", "Loading..."))
    mount(recordElements, likeModal.el)
    const recordShareModal = createRecordShareModal(currentFile.id, embed)
    mount(recordElements, recordShareModal.el)
    const editModal = new EditModal("editModal", "EDIT TITLE", el("input#name.input inputEdit", { type: "text", name: "name", value: currentFile.title, maxlength: 200, style: "width: 100%" }), "SAVE", editRecord)
    mount(recordElements, editModal.el)

    mount(recordElements,
        el(".row", { style: "padding: 10px 15px 10px 15px;" },
            el(".col noSpace", { style: "overflow: hidden; padding-right: 5px; position: relative" },
            isMyRecord ?
                el(".clickable border-bottom rounded border-secondary row justify-content-between", { id: "divEdit", 'data-bs-toggle': "modal", 'data-bs-target': "#editModal", style: "padding: 5px; margin: 0px 0px 10px 0px;" },
                    el(".col wordwrap", { align: "left", style: "padding: 0px;" },
                        el("h3.font-weight-light", { style: "margin: 0px;" }, el("b#record-title", { textContent: currentFile.title }))
                    ),
                    el(".col-auto noPadding", { align: "right", style: "vertical-align: middle;" }, el("span.badge bg-secondary", { textContent: 'EDIT' }))
                )
                : el(".wordwrap", el("h4.font-weight-light", { style: "margin-bottom: 0px" }, el("b", { textContent: currentFile.title })))
            ),
            el(".col-auto", {style: "padding: 0px 0px 0px 15px;" },
            playlist.playlistType == "default" ?
                el(".clickable", { onclick: Audio.toggle, id:"playButton"}, el("i", { className: `playBtn fas fa-${button}`, style: "font-size: 20px; padding-top: 20px; color: #ffffff;" }))
                : {}
            )
        )
    )
    // Audio player
    if (currentFile.status == "OK") {
        if (playlist.playlistType != "default") {
            mount(recordElements,
                el(".row", { style: "padding: 10px 0px;" },
                    el(".col clickable highlight-only-when-touched center", el("i.naviBtn fas fa-fast-backward", { onclick: Audio.moveToPrevious })),
                    el(".col clickable highlight-only-when-touched center", el(`i.playBtn naviBtn fas fa-${button}`, { onclick: Audio.toggle })),
                    el(".col clickable highlight-only-when-touched center", el("i.naviBtn fas fa-fast-forward", { onclick: Audio.moveToNextRecord }))
                )
            )
        }
        mount(recordElements, el("#timeSeriesEvaluatedScoreChart"))
        mount(recordElements, el(".fluid-container row", { style: "margin: 10px 10px 0 0; height: 23px; text-align: justify;" }, el("font", { size: "3" }, buildBadge(currentFile))))
    }
    else if (currentFile.status == "Preprocessing") {
        mount(recordElements, el("", el("h5.recordStatusNotOK", { innerHTML: "This record will be available soon. <br/>Please give us a little more time." })))
    }
    // Like, share, and menu buttons
    const likeIcon = el("i.far fa-heart#likeIcon")
    const likeButton = el("button.modalBtn", { type: "button" }, likeIcon)
    if (me.isDefined) setAttr(likeButton, { id: "likeButton", onclick: () => LIKE.toggle(currentFile.id)})
    else {
        setAttr(likeButton, { id: "popoverBtn", tabindex: "0"} )
        setSignInModal(likeButton, "Like this record?", "to mark a record you like!")
    }
    const container =
        el(".fluid-container row", { style: "padding: 5px; text-align: justify;" },
            el(".float-start", likeButton),
            el(".float-start", { style: "padding: 12px 0px 5px 10px;" },
                el("font", { size: "2" },
                    el("p.likesContent#likesContent"),
                    el("p.likesContent", { style: "padding: 0 4px;"}, "•"),
                    el("p.likesContent", getSingularOrPlural(currentFile.pageview, "view") ),
                    el("p.likesContent", { style: "padding-left: 10px;" }, getTimeAgo(currentFile.uploadedAt) )
                )
            ),
            el(".col", { style: "text-align: right; padding-left: 5px; padding-right: 10px" },
                el("button.modalBtn clickable", { type: "button", 'data-bs-toggle': "modal", 'data-bs-target': "#shareModal" }, el("i.fas fa-share-alt"))),
        )

    const buttons: ModalButton[] = []
    if (isMyRecord) buttons.push({
        icon: el("i.far fa-trash-alt fa-fw"),
        text: "Delete",
        modal: createRecordDeletionModal(currentFile, () => {
            if (Audio.player != undefined) Audio.player.pause() // can't use optional chaining in iOS 12.5.5
            Audio.hidePlayer()
            openOrReturnToUserPage(uploader.id)
        })
    })
    else buttons.push(createReportUserButton(uploader, maybeMeIsMe(me) ? me.get.id : undefined)) // can't use optional chaining in iOS 12.5.5
    mount(container, el(".col-auto", { style: "padding: 0 5px; align-right" }, createMenuButton("more_contents", buttons, recordElements)))
    mount(recordElements, container)

    // Uploader icon, name and follow button
    const uploaderRow =
        el(".row", { style: "padding: 0 15px 15px 15px" },
            el(".col clickable row",
                el(".col-auto noPadding",
                    el("img.rounded-circle", { onclick: () => openUserPageOnClick(uploader.id), src: uploader.imgUrl, style: "height: 2.5rem; width: 2.5rem;", onerror: (e: Event) => { (e.target as HTMLImageElement).src='/public/img/icon200x200.png' } })
                ),
                el(".col", { style: "height: 2.5rem", onclick: () => openUserPageOnClick(uploader.id) },
                    el("h6.vertical-center-in-div", { style: "margin-bottom:0;", textContent: uploader.name })
                )
            ),
            isMyRecord ? {} : new RecordPageFollowManager(uploader.id, curPost.amIFollowed, me.isDefined)
        )
    mount(recordElements, uploaderRow)

    // Modal: shares
    mount(recordElements, el("#encodeTo128ToPaste"))

    const textAreaField = el("textarea.autoExpand#commentText", { rows: '1', 'data-min-rows': '1', placeholder: "Write a public comment...", name: "comment", maxlength: "140" } )
    if (!me.isDefined) setSignInModal(textAreaField, "Want to comment?", "to leave a comment!")
    mount(recordElements,
        el("", { align: "center" },
            el(".bootstrap snippets",
                el(".comment-wrapper",
                    el(".panel panel-info",
                        el(".panel-body",
                            el(".fluid-container row", { style: "width:100%" },
                                el(".float-start col", { style: "margin-right: 10px;" },
                                    el(".textarea", { style: "margin-left: 0px" }, textAreaField),
                                    el("small.float-end text-muted", { id: "commentCharactersLeft", textContent: "140" }) ),
                                el(".col-auto postComment float-end ms-auto",
                                    el(`button.btn btn-sm btn-default${(me.isDefined ? "#commentButton": "")}`,
                                        { type: "submit", textContent: "comment", disabled: true,
                                          onclick: () => { if (me.isDefined) postComment(currentFile.id, me, (document.getElementById("commentText") as HTMLInputElement).value) } } ) ) ),
                            el(".clearfix"),
                            el("ul.comment-list", buildCollapseComments(comments, numberOfCommentsToShow, me, currentFile, "commentCollapse", "commentBtnCollapse") )
                        )
                    )
                )
            )
        )
    )

    return el("", recordElements)
}

const buildCollapseComments = (originalList: CommentWithUser[], numOfItem: number, me: MaybeUser, currentFile: AudioRecord, collapseId: string, buttonId: string) => {

    const displayedRecords = el("div.container-fluid noSpace")
    const collapsedRecords = el(`div.multi-collapse-${collapseId} collapse noSpace#${collapseId}`)
    const list = originalList.filter(item => !isBlocked(item.user.id))
    list.forEach( (item, index) => {
        const commentContent = buildCommentContent(item, me, currentFile.id)
        if (index < numOfItem) mount(displayedRecords, commentContent)
        else mount(collapsedRecords, commentContent)
    })
    mount(displayedRecords, collapsedRecords)

    const collapseComments = el("div", { style: "padding: 0px; margin: 10px 0px 20px 0px"}, displayedRecords)
    if (list.length > numOfItem) {
        const toggleButton =
            el("a", { "data-bs-toggle": "collapse", "data-bs-target": `.multi-collapse-${collapseId}`, role: "button", "aria-expanded": "false", "aria-control": `${collapseId}Top ${collapseId}` },
                el("p.collapseListItem clickable", { id: buttonId, style: "margin-top: -20px"},
                    el("i.fas fa-angle-down", { style: "padding-right: 5px" }),
                    getShowMoreText("comment", list.length - numOfItem)
                )
            )
        mount(collapseComments,toggleButton)
    }

    return collapseComments
}
